import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://todo-backend.abacies.com';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add interceptor to add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }

    console.log('API Request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      fullURL: `${config.baseURL}${config.url}`,
      headers: config.headers,
      data: config.data
    });

    // Format dates for task API calls
    if (config.url.includes('/tasks/') && config.data) {
      const data = { ...config.data };
      
      // Convert Date objects to YYYY-MM-DD format
      if (data.date instanceof Date) {
        data.date = data.date.toISOString().split('T')[0];
      }
      
      // Convert Time objects if needed
      if (data.time instanceof Date) {
        data.time = data.time.toTimeString().slice(0, 5);
      }
      
      config.data = data;
    }
    
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for better error handling
api.interceptors.response.use(
  (response) => {
    console.log('API Response:', {
      status: response.status,
      statusText: response.statusText,
      url: response.config.url,
      method: response.config.method?.toUpperCase(),
      dataType: typeof response.data,
      dataLength: Array.isArray(response.data) ? response.data.length : 'N/A',
      data: response.data
    });
    return response;
  },
  (error) => {
    console.error('API Response Error:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      data: error.response?.data,
      headers: error.response?.headers
    });
    
    // Handle specific error cases
    if (error.response?.status === 401) {
      console.warn('Authentication failed - token may be invalid or expired');
      console.warn('Current token:', localStorage.getItem('token')?.substring(0, 20) + '...');
      
      // Automatically handle authentication expiry
      console.log('Automatically logging out and redirecting to login...');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Trigger a custom event to notify other components
      window.dispatchEvent(new Event('userLogout'));
      
      // Redirect to login page
      if (typeof window !== 'undefined' && window.location) {
        // Only redirect if we're not already on the login page
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
    } else if (error.response?.status === 403) {
      console.warn('Access forbidden - user may not have permission');
    } else if (error.response?.status === 404) {
      console.warn('Endpoint not found - check API URL');
    } else if (error.response?.status >= 500) {
      console.warn('Server error - backend may be down or experiencing issues');
    }
    
    return Promise.reject(error);
  }
);

// Auth services
export const authService = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    if (response.data && response.data.auth_token) {
      localStorage.setItem('token', response.data.auth_token);
    }
    return response.data;
  },
  
  acceptInvite: async (userData) => {
    // According to CSV: PUT /user/register_from_invitation with first_name, last_name, email, password, confirm_password
    return await api.put('/user/register_from_invitation', userData);
  },
  
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },
  
  getProfile: async () => {
    return await api.get('/auth/profile');
  },
  
  createInvite: async (inviteData) => {
    // According to CSV: POST /user/invite with first_name, last_name, email, role_id
    return await api.post('/user/invite', inviteData);
  },
  
  getInvites: async () => {
    return await api.get('/auth/invites');
  }
};

// Task services
export const taskService = {
  getTasks: async (filters = {}) => {
    const { page = 1, per_page = 50, ...otherFilters } = filters;
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: per_page.toString(),
      ...otherFilters
    });
    return await api.get(`/task/list-all-tasks?${params.toString()}`);
  },
  
  getTask: async (id) => {
    return await api.get(`/tasks/${id}`);
  },
  
  createTask: async (taskData) => {
    console.log('Creating task with data:', taskData);
    
    const formattedData = { ...taskData };
    
    // Handle due_date field formatting
    if (formattedData.due_date) {
      if (formattedData.due_date instanceof Date) {
        formattedData.due_date = formattedData.due_date.toISOString().split('T')[0];
      } else if (typeof formattedData.due_date === 'string') {
        try {
          const dateObj = new Date(formattedData.due_date);
          if (!isNaN(dateObj.getTime())) {
            formattedData.due_date = dateObj.toISOString().split('T')[0];
          }
        } catch (e) {
          console.error('Invalid due_date format:', e);
        }
      }
    }
    
    console.log('Formatted data being sent to API:', formattedData);
    
    // Get project_id from form data - required field
    // The project_id should come from the TaskModal, but if not present, try to get it from formData
    let projectId = taskData.project_id || formattedData.project_id;
    
    if (!projectId) {
      throw new Error('Project ID is required');
    }
    
    // Remove project_id from the body since it's in the URL
    const { project_id, ...requestData } = formattedData;
    
    console.log(`Posting to /task/${projectId} with data:`, requestData);
    
    const response = await api.post(`/task/${projectId}`, requestData);
    
    // Check if the response contains an error message even with 200 status
    if (response.data && response.data.message && response.data.status && response.data.status !== 201) {
      console.error('API returned error in response body:', response.data);
      const error = new Error(response.data.message);
      error.response = {
        status: response.data.status,
        data: response.data
      };
      throw error;
    }
    
    return response;
  },
  
  updateTask: async (id, taskData, projectId) => {
    const formattedData = { ...taskData };
    
    // Ensure we have a project_id
    if (!projectId && taskData.project_id) {
      projectId = taskData.project_id;
    }
    
    if (!projectId) {
      throw new Error('Project ID is required for task update');
    }
    
    if (formattedData.due_date) {
      if (formattedData.due_date instanceof Date) {
        formattedData.due_date = formattedData.due_date.toISOString().split('T')[0];
      } else if (typeof formattedData.due_date === 'string') {
        try {
          const dateObj = new Date(formattedData.due_date);
          if (!isNaN(dateObj.getTime())) {
            formattedData.due_date = dateObj.toISOString().split('T')[0];
          }
        } catch (e) {
          console.error('Invalid due_date format:', e);
        }
      }
    }
    
    if (formattedData.time) {
      if (formattedData.time instanceof Date) {
        const hours = formattedData.time.getHours().toString().padStart(2, '0');
        const minutes = formattedData.time.getMinutes().toString().padStart(2, '0');
        formattedData.time = `${hours}:${minutes}`;
      } else if (typeof formattedData.time === 'string') {
        const timeRegex = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/;
        if (!timeRegex.test(formattedData.time)) {
          try {
            const timeParts = formattedData.time.split(':');
            if (timeParts.length >= 2) {
              const hours = timeParts[0].padStart(2, '0');
              const minutes = timeParts[1].padStart(2, '0');
              formattedData.time = `${hours}:${minutes}`;
            }
          } catch (e) {
            console.error('Invalid time format:', e);
          }
        }
      }
    }
    
    // Remove project_id from body since it's in the URL
    const { project_id, ...requestData } = formattedData;
    
    console.log(`Updating task ${id} in project ${projectId} with data:`, requestData);
    
    return await api.put(`/task/${projectId}/${id}`, requestData);
  },
  
  patchTask: async (id, partialData) => {
    const formattedData = { ...partialData };
    
    if (formattedData.due_date) {
      if (formattedData.due_date instanceof Date) {
        formattedData.due_date = formattedData.due_date.toISOString().split('T')[0];
      } else if (typeof formattedData.due_date === 'string') {
        try {
          const dateObj = new Date(formattedData.due_date);
          if (!isNaN(dateObj.getTime())) {
            formattedData.due_date = dateObj.toISOString().split('T')[0];
          }
        } catch (e) {
          console.error('Invalid due_date format:', e);
        }
      }
    }
    
    if (formattedData.time) {
      if (formattedData.time instanceof Date) {
        const hours = formattedData.time.getHours().toString().padStart(2, '0');
        const minutes = formattedData.time.getMinutes().toString().padStart(2, '0');
        formattedData.time = `${hours}:${minutes}`;
      } else if (typeof formattedData.time === 'string') {
        const timeRegex = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/;
        if (!timeRegex.test(formattedData.time)) {
          try {
            const timeParts = formattedData.time.split(':');
            if (timeParts.length >= 2) {
              const hours = timeParts[0].padStart(2, '0');
              const minutes = timeParts[1].padStart(2, '0');
              formattedData.time = `${hours}:${minutes}`;
            }
          } catch (e) {
            console.error('Invalid time format:', e);
          }
        }
      }
    }
    
    // For partial updates (like status changes), we need to determine the project_id
    // This is a limitation of the current API design - we'll use a fallback approach
    // In a real application, you might want to store the project_id with the task
    // or make the API more flexible to accept PATCH /task/<task_id>
    
    console.log(`Patching task ${id} with data:`, formattedData);
    
    // Try the simple approach first (may work if API supports it)
    try {
      return await api.patch(`/task/${id}`, formattedData);
    } catch (error) {
      // If that fails, we need to find the project_id and use the full endpoint
      // This is a workaround - in practice you'd want to track project_id better
      console.warn('Simple PATCH failed, trying with project_id approach');
      
      // For now, we'll assume project_id = 1 for status updates
      // This should be improved by either:
      // 1. Making the API accept PATCH /task/<task_id>
      // 2. Storing project_id in the task object consistently
      // 3. Making a separate API call to get task details first
      const projectId = 1;
      
      return await api.put(`/task/${projectId}/${id}`, formattedData);
    }
  },
  
  deleteTask: async (id, projectId = null) => {
    console.log('=== DELETE TASK API CALL ===');
    console.log('Task ID:', id);
    console.log('Project ID:', projectId);
    
    // According to CSV documentation, the endpoint is DELETE /task/<int:task_id>
    // This means we only need the task ID, not the project ID
    const endpoint = `/task/${id}`;
    console.log('Delete endpoint:', endpoint);
    
    try {
      const response = await api.delete(endpoint);
      console.log('Delete task response:', response);
      return response;
    } catch (error) {
      console.error('Delete task API error:', error);
      console.error('Endpoint used:', endpoint);
      throw error;
    }
  }
};

// User services
export const userService = {
  getUsers: async (page = 1, per_page = 10, time_zone = 'UTC') => {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: per_page.toString(),
      time_zone: time_zone
    });
    return await api.get(`/user/user/paginated_list?${params.toString()}`);
  },
  
  getUser: async (id) => {
    return await api.get(`/user/${id}`);
  },
  
  updateUser: async (id, userData) => {
    return await api.put(`/user/${id}`, userData);
  },
  
  deleteUser: async (id) => {
    return await api.delete(`/user/${id}`);
  },
  
  getCurrentUser: async () => {
    return await api.get('/auth/profile');
  }
};

// Project services
export const projectService = {
  getProjects: async () => {
    return await api.get('/project/projet_name');
  },
  
  getProject: async (id) => {
    return await api.get(`/project/${id}`);
  },
  
  createProject: async (projectData) => {
    console.log('Creating project with data:', projectData);
    
    const response = await api.post('/project/', projectData);
    
    // Check if the response contains an error message even with 200 status
    if (response.data && response.data.message && response.data.status && response.data.status !== 201) {
      console.error('API returned error in response body:', response.data);
      const error = new Error(response.data.message);
      error.response = {
        status: response.data.status,
        data: response.data
      };
      throw error;
    }
    
    return response;
  },
  
  updateProject: async (id, projectData) => {
    console.log('Updating project with data:', projectData);
    
    const response = await api.put(`/project/${id}`, projectData);
    
    return response;
  },
  
  deleteProject: async (id) => {
    return await api.delete(`/project/${id}`);
  }
};

export default api; 